import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Heart, Shield, Users, HeartPulse, Home, Rocket, UtensilsCrossed, GraduationCap, Shirt, UserPlus, ListChecks, PenLine, HandCoins } from 'lucide-react';

const VERTICALES = [
  { name: 'Salud', icon: HeartPulse, desc: 'Farmacia, consultas, laboratorios, cirugías y salud integral.', color: 'bg-rose-100 text-rose-600' },
  { name: 'Vivienda', icon: Home, desc: 'Renta, depósitos, mejoras y acceso a un hogar digno.', color: 'bg-sky-100 text-sky-600' },
  { name: 'Emprendimiento', icon: Rocket, desc: 'Capital semilla para tu negocio o proyecto productivo.', color: 'bg-violet-100 text-violet-600' },
  { name: 'Alimentación', icon: UtensilsCrossed, desc: 'Despensas y seguridad alimentaria para ti y tu familia.', color: 'bg-amber-100 text-amber-600' },
  { name: 'Educación', icon: GraduationCap, desc: 'Colegiaturas, cursos, certificaciones y material escolar.', color: 'bg-emerald-100 text-emerald-600' },
  { name: 'Vestimenta', icon: Shirt, desc: 'Ropa y accesorios para expresar quien eres.', color: 'bg-pink-100 text-pink-600' },
];

const PASOS = [
  { icon: UserPlus, title: '1. Regístrate', desc: 'Crea tu cuenta como persona física o moral. Es gratis y seguro.' },
  { icon: ListChecks, title: '2. Elige tu tanda', desc: 'Explora las tandas activas por vertical (Salud, Vivienda, Emprendimiento y más) e indica tu nivel de necesidad del 0 al 100.' },
  { icon: PenLine, title: '3. Firma tu compromiso', desc: 'Firma digitalmente el contrato de adhesión y valida tu identidad con tu INE. Une admin revisará y aprobará tu solicitud.' },
  { icon: HandCoins, title: '4. Aporta y recibe', desc: 'Realiza tus aportaciones cada ciclo, sube tu comprobante y recibe el fondo completo cuando sea tu turno según tu prioridad de necesidad.' },
];

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="space-y-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden rounded-3xl bg-gradient-to-br from-secondary/20 via-white to-primary/20 dark:from-secondary/10 dark:via-background dark:to-primary/10 border border-white/50">
        <div className="container px-4 text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-black font-heading tracking-tight mb-6 text-foreground">
            Ahorro <span className="text-primary">Comunitario</span>,<br />
            Impacto <span className="text-secondary">Real</span>.
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Tanducibless es el espacio seguro para que nuestra comunidad gestione tandas deducibles de impuestos.
            <br className="hidden md:block" /> Organizado por ATLASST para todes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/dashboard">
                <Button size="lg" data-testid="hero-dashboard-btn" className="rounded-full text-lg px-8 py-6 shadow-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-transform hover:scale-105">
                  Ir a mi Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/signup">
                  <Button size="lg" data-testid="hero-signup-btn" className="rounded-full text-lg px-8 py-6 shadow-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-transform hover:scale-105">
                    Unirme a la comunidad
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" data-testid="hero-login-btn" className="rounded-full text-lg px-8 py-6 border-2">
                    Ya tengo cuenta
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Abstract Shapes */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
      </section>

      {/* Cómo funciona */}
      <section className="space-y-10" data-testid="how-it-works-section">
        <div className="text-center space-y-3">
          <h2 className="text-3xl md:text-4xl font-black font-heading">¿Cómo funciona <span className="text-primary">Tanducibless</span>?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Una tanda es un fondo de ahorro colectivo: todes aportan una cantidad fija cada ciclo (semanal, quincenal o mensual)
            y cada persona recibe el monto total en su turno. Aquí, quien más lo necesita, recibe primero.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {PASOS.map((paso) => (
            <div key={paso.title} className="p-6 rounded-3xl bg-white/60 dark:bg-white/5 border border-white/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 bg-primary/15 rounded-2xl flex items-center justify-center mb-4 text-primary">
                <paso.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold font-heading mb-2">{paso.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{paso.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Verticales */}
      <section className="space-y-10" data-testid="verticals-section">
        <div className="text-center space-y-3">
          <h2 className="text-3xl md:text-4xl font-black font-heading">Nuestras <span className="text-secondary">Verticales</span></h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Cada tanda pertenece a una vertical de apoyo. Elige la que responde a tu necesidad.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {VERTICALES.map((v) => (
            <div key={v.name} data-testid={`vertical-card-${v.name.toLowerCase()}`} className="p-6 rounded-3xl bg-white/60 dark:bg-white/5 border border-white/30 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${v.color}`}>
                <v.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold font-heading mb-2">{v.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="grid md:grid-cols-3 gap-8">
        <div className="p-8 rounded-3xl bg-white/50 dark:bg-white/5 border border-white/20 shadow-sm hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-6 text-primary">
            <Users className="w-6 h-6" />
          </div>
          <h3 className="text-2xl font-bold font-heading mb-3">Comunidad Segura</h3>
          <p className="text-muted-foreground">Un espacio validado para personas trans y aliades. Aquí todes somos bienvenides.</p>
        </div>
        <div className="p-8 rounded-3xl bg-white/50 dark:bg-white/5 border border-white/20 shadow-sm hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-6 text-secondary-foreground">
            <Heart className="w-6 h-6" />
          </div>
          <h3 className="text-2xl font-bold font-heading mb-3">Apoyo Mutuo</h3>
          <p className="text-muted-foreground">Las tandas se organizan por necesidad. Quien más lo necesita, recibe primero.</p>
        </div>
        <div className="p-8 rounded-3xl bg-white/50 dark:bg-white/5 border border-white/20 shadow-sm hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-600">
            <Shield className="w-6 h-6" />
          </div>
          <h3 className="text-2xl font-bold font-heading mb-3">Transparencia Total</h3>
          <p className="text-muted-foreground">Sube tus comprobantes, monitorea el progreso y gestiona tus aportaciones fácilmente.</p>
        </div>
      </section>
    </div>
  );
}
